import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import { ErrorBanner, PrimaryButton } from "../../components/crm/Form";
import PlanForm from "../../components/billing/PlanForm";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";

const WRITE_ROLES = ["owner", "ops"];
const CADENCE_LABEL = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
};

function fmtCents(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function PlansList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [list, setList] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/protection-plans/", {
        params: { include_inactive: includeInactive ? "true" : "false" },
      });
      setList(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  return (
    <Layout title="Protection Plans" subtitle="Billing">
      <EntityHeader
        overline="catalog"
        title="Protection Plans"
        subtitle="Recurring service-plan offerings — pricing is snapshotted at enrollment."
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="inline-flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            data-testid="plans-include-inactive"
            className="h-4 w-4 rounded border-slate-300 text-amber-600"
          />
          Show inactive
        </label>
        {canWrite && (
          <PrimaryButton
            onClick={() => setOpen(true)}
            data-testid="plans-create-button"
            className="ml-auto"
          >
            <PlusIcon size={14} weight="bold" />
            New Plan
          </PrimaryButton>
        )}
      </div>

      <ErrorBanner message={err} />

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-card">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              <th className="px-5 py-3">Code</th>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Cadence</th>
              <th className="px-5 py-3 text-right">Default amount</th>
              <th className="px-5 py-3 text-right">Active subs</th>
              <th className="px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-5 py-12 text-center text-sm text-slate-500"
                  data-testid="plans-empty"
                >
                  No plans yet.
                </td>
              </tr>
            )}
            {list.map((p) => (
              <tr
                key={p.id}
                className="cursor-pointer hover:bg-slate-50/60"
                onClick={() => nav(`/protection-plans/${p.id}`)}
                data-testid={`plan-row-${p.id}`}
              >
                <td className="px-5 py-3 font-data text-xs text-slate-700">
                  {p.code}
                </td>
                <td className="px-5 py-3 font-medium text-slate-900">
                  {p.name}
                </td>
                <td className="px-5 py-3 text-xs text-slate-700">
                  {CADENCE_LABEL[p.default_billing_cadence]}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-900 text-right">
                  {fmtCents(p.default_amount_cents)}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700 text-right">
                  {p.active_subscription_count}
                </td>
                <td className="px-5 py-3 text-xs">
                  {p.is_active ? (
                    <span className="inline-flex items-center rounded-md border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-md border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-700">
                      Inactive
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canWrite && (
        <PlanForm
          open={open}
          onClose={() => setOpen(false)}
          onSaved={(p) => {
            setOpen(false);
            nav(`/protection-plans/${p.id}`);
          }}
        />
      )}
    </Layout>
  );
}
